import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Chip,
  Avatar,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { 
  Save, 
  Refresh, 
  MyLocation,
  LocationOn,
  Edit,
} from '@mui/icons-material';
import { url } from '../../constants';
import { useNavigate } from 'react-router-dom';

// Image compression utility
const compressImage = (file, maxSizeKB = 100) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Resize if too large (max 800px)
        const MAX_DIMENSION = 800;
        if (width > height) {
          if (width > MAX_DIMENSION) {
            height *= MAX_DIMENSION / width;
            width = MAX_DIMENSION;
          }
        } else {
          if (height > MAX_DIMENSION) {
            width *= MAX_DIMENSION / height;
            height = MAX_DIMENSION;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        let quality = 0.9;
        let dataUrl = canvas.toDataURL('image/jpeg', quality);

        // Compress until under 100KB
        while (Math.round((dataUrl.length * 3) / 4) > maxSizeKB * 1024 && quality > 0.1) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL('image/jpeg', quality);
        }

        resolve(dataUrl);
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

// ✅ Helper to fix the missing image path
const getImageUrl = (imagePath) => {
  if (!imagePath) return '';
  if (imagePath.startsWith('data:image') || imagePath.startsWith('http')) {
    return imagePath;
  }
  return `${url}${imagePath}`;
};

const VendorDashboard = () => {
  const [vendor, setVendor] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [apiError, setApiError] = useState('');
  const [apiSuccess, setApiSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [updatingLocation, setUpdatingLocation] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const navigate = useNavigate();

  // Load vendor from localStorage on mount
  useEffect(() => {
    const storedVendor = localStorage.getItem('vendor_data');
    if (storedVendor) {
      const v = JSON.parse(storedVendor);
      setVendor(v);
      setFormData({
        business_name: v.business_name || '',
        owner_name: v.owner_name || '',
        phone: v.phone || '',
        alternate_phone: v.alternate_phone || '',
        business_type: v.business_type || '',
        description: v.description || '',
        street: v.address?.street || '',
        city: v.address?.city || '',
        state: v.address?.state || '',
        pincode: v.address?.pincode || '',
        categories: v.categories || [],
        profile_image: v.profile_image || '', 
      });
    }
    setLoading(false);
  }, []);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${url}/users/get_categories`);
        if (!res.ok) throw new Error('Failed to fetch categories');
        const data = await res.json();
        setCategories(data);
      } catch (err) {
        setApiError(err.message);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const compressedBase64 = await compressImage(file, 100);
      setFormData(prev => ({ ...prev, profile_image: compressedBase64 }));
      setApiError('');
    } catch (error) {
      console.error("Error compressing image:", error);
      setApiError("Failed to process the image. Please try another one.");
    }
  };

  const handleUpdateLocation = async () => {
    if (!navigator.geolocation) {
      setApiError('Geolocation is not supported by this browser');
      return;
    }
    setUpdatingLocation(true);
    setApiError('');
    setApiSuccess('');
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        try {
          const token = localStorage.getItem('vendor_token');
          const res = await fetch(`${url}/vendors/me`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              location: {
                type: "Point",
                coordinates: [lng, lat]
              }
            }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || 'Failed to update location');
          setVendor(data);
          localStorage.setItem('vendor_data', JSON.stringify(data));
          setApiSuccess(`✅ Location updated: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
        } catch (err) {
          setApiError(err.message);
        } finally {
          setUpdatingLocation(false);
        }
      },
      (error) => {
        setApiError(`Location access denied: ${error.message}`);
        setUpdatingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000,
      }
    );
  };

  const handleToggleCategory = (cateId) => {
    setFormData((prev) => {
      const selected = prev.categories || [];
      if (selected.includes(cateId)) {
        return { ...prev, categories: selected.filter((id) => id !== cateId) };
      }
      return { ...prev, categories: [...selected, cateId] };
    });
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setApiError('');
    setApiSuccess('');
  };

  const handleToggleEdit = () => {
    if (isEditing) {
      const storedVendor = localStorage.getItem('vendor_data');
      if (storedVendor) {
        const v = JSON.parse(storedVendor);
        setFormData({
          business_name: v.business_name || '',
          owner_name: v.owner_name || '',
          phone: v.phone || '',
          alternate_phone: v.alternate_phone || '',
          business_type: v.business_type || '',
          description: v.description || '',
          street: v.address?.street || '',
          city: v.address?.city || '',
          state: v.address?.state || '',
          pincode: v.address?.pincode || '',
          categories: v.categories || [],
          profile_image: v.profile_image || '', 
        });
      }
    }
    setIsEditing(!isEditing);
    setApiError('');
    setApiSuccess('');
  };

  const handleSave = async () => {
    setSaving(true);
    setApiError('');
    setApiSuccess('');
    const payload = {
      business_name: formData.business_name || null,
      owner_name: formData.owner_name || null,
      phone: formData.phone || null,
      alternate_phone: formData.alternate_phone || null,
      business_type: formData.business_type || null,
      description: formData.description || null,
      address: {
        street: formData.street || null,
        city: formData.city || null,
        state: formData.state || null,
        pincode: formData.pincode || null,
      },
      categories: formData.categories || [],
      profile_image: formData.profile_image || null, 
    };
    try {
      const token = localStorage.getItem('vendor_token');
      const res = await fetch(`${url}/vendors/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to update vendor');
      setVendor(data);
      localStorage.setItem('vendor_data', JSON.stringify(data));
      setApiSuccess('✅ Profile updated successfully!');
      
      // Update form data to point to the new saved image URL
      setFormData(prev => ({...prev, profile_image: data.profile_image}));
      
      setIsEditing(false);
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('vendor_token');
      const res = await fetch(`${url}/vendors/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to refresh');
      setVendor(data);
      localStorage.setItem('vendor_data', JSON.stringify(data));
      setFormData({
        business_name: data.business_name || '',
        owner_name: data.owner_name || '',
        phone: data.phone || '',
        alternate_phone: data.alternate_phone || '',
        business_type: data.business_type || '',
        description: data.description || '',
        street: data.address?.street || '',
        city: data.address?.city || '',
        state: data.address?.state || '',
        pincode: data.address?.pincode || '',
        categories: data.categories || [],
        profile_image: data.profile_image || '', 
      });
      setApiSuccess('✅ Data refreshed from server');
      setIsEditing(false);
    } catch (err) {
      setApiError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !vendor) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading vendor data...</Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth={isMobile ? "sm" : "lg"} sx={{ py: isMobile ? 2 : 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: isMobile ? 2 : 4, 
          borderRadius: 3,
          [theme.breakpoints.down('sm')]: { p: 1.5 }
        }}
      >
        {/* ✅ FIXED Header Layout: Proper Flexbox prevents overlapping */}
        <Box sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 3,
          gap: 2
        }}>
          
          {/* Left: Avatar + Title */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Avatar
                src={getImageUrl(isEditing ? formData.profile_image : vendor.profile_image)}
                sx={{ 
                  width: { xs: 64, md: 80 }, 
                  height: { xs: 64, md: 80 }, 
                  bgcolor: 'primary.main',
                  border: formData.profile_image ? '2px solid #1a7f3a' : 'none'
                }}
              >
                {vendor.business_name?.[0] || 'V'}
              </Avatar>
              {isEditing && (
                <Button 
                  variant="outlined" 
                  component="label" 
                  size="small" 
                  sx={{ 
                    fontSize: '0.65rem', 
                    py: 0.2, 
                    px: 1, 
                    minWidth: 'auto',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Upload Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageUpload}
                  />
                </Button>
              )}
            </Box>
            
            <Box>
              <Typography 
                variant={isMobile ? "h5" : "h4"} 
                fontWeight={700}
                sx={{ 
                  [theme.breakpoints.down('sm')]: { fontSize: '1.25rem' }
                }}
              >
                Vendor Dashboard
              </Typography>
              <Typography 
                variant={isMobile ? "body1" : "h6"} 
                color="primary"
                sx={{ 
                  [theme.breakpoints.down('sm')]: { fontSize: '0.9rem' }
                }}
              >
                ID: {vendor.vendor_id}
              </Typography>
              <Chip 
                label={vendor.is_verified ? 'Verified' : 'Not Verified'} 
                color={vendor.is_verified ? 'success' : 'default'}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Box>

          {/* ✅ Right: Action Buttons (No negative margins, proper wrapping) */}
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
            mt: { xs: 1, md: 0 }
          }}>
            <Button
              variant="contained"
              startIcon={<Save />}
              size="small"
              onClick={() => navigate('/vendor/deals/view')}
              sx={{ 
                minWidth: 100,
                fontSize: '0.8rem',
                px: 1.5,
                py: 0.75
              }}
            >
              Your Deals
            </Button>
            <Button
              variant="outlined"
              startIcon={<MyLocation fontSize="small" />}
              onClick={handleUpdateLocation}
              disabled={saving || loading || updatingLocation}
              size="small"
              sx={{ 
                minWidth: 100,
                fontSize: '0.8rem',
                px: 1.5,
                py: 0.75
              }}
            >
              {updatingLocation ? (
                <CircularProgress size={16} sx={{ mr: 0.5 }} />
              ) : (
                'Update Location'
              )}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh fontSize="small" />}
              onClick={handleRefresh}
              disabled={saving || loading || updatingLocation}
              size="small"
              sx={{ 
                fontSize: '0.8rem',
                px: 1.5,
                py: 0.75
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={isEditing ? <Save fontSize="small" /> : <Edit fontSize="small" />}
              onClick={isEditing ? handleSave : handleToggleEdit}
              disabled={saving || updatingLocation || (!isEditing && loading)}
              size="small"
              sx={{ 
                fontSize: '0.8rem',
                px: 1.5,
                py: 0.75
              }}
            >
              {saving ? 'Saving...' : (isEditing ? 'Save' : 'Edit')}
            </Button>
          </Box>
        </Box>

        {/* Status Messages */}
        {apiError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setApiError('')}>
            {apiError}
          </Alert>
        )}
        {apiSuccess && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setApiSuccess('')}>
            {apiSuccess}
          </Alert>
        )}

        {/* Current Location Display */}
        <Paper sx={{ 
          p: { xs: 2, md: 3 }, 
          mb: 3, 
          bgcolor: 'grey.50',
          [theme.breakpoints.down('sm')]: { p: 1.5 }
        }}>
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"} 
            sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <LocationOn color="primary" />
            Current Location
          </Typography>
          <Typography variant="body1">
            Lat: {vendor.location?.coordinates?.[1]?.toFixed(4) || 'N/A'} | 
            Lng: {vendor.location?.coordinates?.[0]?.toFixed(4) || 'N/A'}
          </Typography>
        </Paper>

        {/* Profile Form */}
        <Grid container spacing={isMobile ? 1.5 : 3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Business Name *"
              name="business_name"
              value={formData.business_name}
              onChange={handleChange}
              disabled={!isEditing}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                readOnly: !isEditing,
                sx: {
                  '&.Mui-disabled': {
                    color: 'text.primary',
                    WebkitTextFillColor: 'text.primary',
                    backgroundColor: 'action.hover'
                  }
                }
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Owner Name"
              name="owner_name"
              value={formData.owner_name}
              onChange={handleChange}
              disabled={!isEditing}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                readOnly: !isEditing,
                sx: {
                  '&.Mui-disabled': {
                    color: 'text.primary',
                    WebkitTextFillColor: 'text.primary',
                    backgroundColor: 'action.hover'
                  }
                }
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              disabled={!isEditing}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                readOnly: !isEditing,
                sx: {
                  '&.Mui-disabled': {
                    color: 'text.primary',
                    WebkitTextFillColor: 'text.primary',
                    backgroundColor: 'action.hover'
                  }
                }
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Alternate Phone"
              name="alternate_phone"
              value={formData.alternate_phone}
              onChange={handleChange}
              disabled={!isEditing}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                readOnly: !isEditing,
                sx: {
                  '&.Mui-disabled': {
                    color: 'text.primary',
                    WebkitTextFillColor: 'text.primary',
                    backgroundColor: 'action.hover'
                  }
                }
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Business Type"
              name="business_type"
              value={formData.business_type}
              onChange={handleChange}
              disabled={!isEditing}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                readOnly: !isEditing,
                sx: {
                  '&.Mui-disabled': {
                    color: 'text.primary',
                    WebkitTextFillColor: 'text.primary',
                    backgroundColor: 'action.hover'
                  }
                }
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email (Read-only)"
              value={vendor.email || 'N/A'}
              InputProps={{ readOnly: true }}
              size={isMobile ? "small" : "medium"}
              sx={{ mb: 1 }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
              disabled={!isEditing}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                readOnly: !isEditing,
                sx: {
                  '&.Mui-disabled': {
                    color: 'text.primary',
                    WebkitTextFillColor: 'text.primary',
                    backgroundColor: 'action.hover'
                  }
                }
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Street"
              name="street"
              value={formData.street}
              onChange={handleChange}
              disabled={!isEditing}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                readOnly: !isEditing,
                sx: {
                  '&.Mui-disabled': {
                    color: 'text.primary',
                    WebkitTextFillColor: 'text.primary',
                    backgroundColor: 'action.hover'
                  }
                }
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
              disabled={!isEditing}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                readOnly: !isEditing,
                sx: {
                  '&.Mui-disabled': {
                    color: 'text.primary',
                    WebkitTextFillColor: 'text.primary',
                    backgroundColor: 'action.hover'
                  }
                }
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="State"
              name="state"
              value={formData.state}
              onChange={handleChange}
              disabled={!isEditing}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                readOnly: !isEditing,
                sx: {
                  '&.Mui-disabled': {
                    color: 'text.primary',
                    WebkitTextFillColor: 'text.primary',
                    backgroundColor: 'action.hover'
                  }
                }
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1 }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Pincode"
              name="pincode"
              value={formData.pincode}
              onChange={handleChange}
              disabled={!isEditing}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                readOnly: !isEditing,
                sx: {
                  '&.Mui-disabled': {
                    color: 'text.primary',
                    WebkitTextFillColor: 'text.primary',
                    backgroundColor: 'action.hover'
                  }
                }
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 1 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"} 
              sx={{ mb: 2, fontWeight: 600 }}
            >
              Categories ({formData.categories?.length || 0} selected)
            </Typography>
            {categoriesLoading ? (
              <CircularProgress size={20} />
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 1, 
                mb: 3,
                maxHeight: 120,
                overflow: 'auto',
                [theme.breakpoints.down('sm')]: { gap: 0.5 }
              }}>
                {categories.map((cat) => {
                  const selected = (formData.categories || []).includes(cat.cate_id);
                  return (
                    <Chip
                      key={cat.cate_id}
                      label={cat.cate_name}
                      onClick={isEditing ? () => handleToggleCategory(cat.cate_id) : undefined}
                      clickable={isEditing}
                      disabled={!isEditing}
                      size={isMobile ? "small" : "medium"}
                      sx={{
                        bgcolor: selected ? cat.cate_color : 'action.selected',
                        border: '2px solid',
                        borderColor: cat.cate_color,
                        color: selected ? '#fff' : 'text.primary',
                        fontWeight: 600,
                        fontSize: { xs: '0.75rem', md: '0.875rem' },
                        cursor: isEditing ? 'pointer' : 'default',
                        '&:hover': isEditing && {
                          bgcolor: selected ? cat.cate_color : 'action.hover',
                        },
                      }}
                    />
                  );
                })}
              </Box>
            )}
          </Grid>

          {/* Stats Cards */}
          <Grid item xs={6} sm={6} md={3}>
            <Paper sx={{ 
              p: { xs: 2, md: 3 }, 
              height: '100%', 
              textAlign: 'center',
              [theme.breakpoints.down('sm')]: { p: 1.5 }
            }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Total Deals
              </Typography>
              <Typography variant={isMobile ? "h5" : "h3"} fontWeight={700}>
                {vendor.total_deals || 0}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Paper sx={{ 
              p: { xs: 2, md: 3 }, 
              height: '100%', 
              textAlign: 'center',
              [theme.breakpoints.down('sm')]: { p: 1.5 }
            }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Status
              </Typography>
              <Chip 
                label={vendor.is_active ? 'Active' : 'Inactive'} 
                color={vendor.is_active ? 'success' : 'error'}
                size={isMobile ? "medium" : "large"}
              />
            </Paper>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Paper sx={{ 
              p: { xs: 2, md: 3 }, 
              height: '100%', 
              textAlign: 'center',
              [theme.breakpoints.down('sm')]: { p: 1.5 }
            }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Verified
              </Typography>
              <Chip 
                label={vendor.is_verified ? 'Verified' : 'Pending'} 
                color={vendor.is_verified ? 'success' : 'warning'}
                size={isMobile ? "medium" : "large"}
              />
            </Paper>
          </Grid>

          <Grid item xs={6} sm={6} md={3}>
            <Paper sx={{ 
              p: { xs: 2, md: 3 }, 
              height: '100%', 
              textAlign: 'center',
              [theme.breakpoints.down('sm')]: { p: 1.5 }
            }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Created
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {vendor.created_at 
                  ? new Date(vendor.created_at).toLocaleDateString('en-IN')  
                  : 'N/A'
                }
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};
export default VendorDashboard;